import React, { useState } from 'react';
import {
  Users, CheckSquare, Calendar, FileCheck, ClipboardList,
  CheckCircle, DollarSign, Video, BarChart3, ShieldCheck, FileText, Eye,
  Briefcase, UserCheck, FileSignature
} from 'lucide-react';

interface ItemTypeSelectorProps {
  onSelectCategory: (category: string) => void;
  onCancel: () => void;
}

interface CategoryOption {
  id: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  section: 'content' | 'tasks' | 'approvals' | 'media' | 'forms';
}

type TabType = 'all' | 'content' | 'tasks' | 'approvals' | 'media' | 'forms';

export default function ItemTypeSelector({ onSelectCategory, onCancel }: ItemTypeSelectorProps) {
  const [activeTab, setActiveTab] = useState<TabType>('all');

  const categories: CategoryOption[] = [
    // Content & Deliverables
    {
      id: 'content',
      label: 'Content Item',
      description: 'Create blog post, service page, or other content',
      icon: <FileText className="w-6 h-6" />,
      color: 'bg-cyan-100 text-cyan-700 hover:bg-cyan-200 border-cyan-300',
      section: 'content'
    },
    {
      id: 'review',
      label: 'Review & Approval',
      description: 'Review assets with attachment and approval workflow',
      icon: <Eye className="w-6 h-6" />,
      color: 'bg-orange-100 text-orange-700 hover:bg-orange-200 border-orange-300',
      section: 'content'
    },
    {
      id: 'video',
      label: 'Video',
      description: 'Video upload or recording task',
      icon: <Video className="w-6 h-6" />,
      color: 'bg-red-100 text-red-700 hover:bg-red-200 border-red-300',
      section: 'media'
    },
    // Tasks & Assignments
    {
      id: 'client_task',
      label: 'Client Task',
      description: 'Task requiring client input or approval',
      icon: <Users className="w-6 h-6" />,
      color: 'bg-blue-100 text-blue-700 hover:bg-blue-200 border-blue-300',
      section: 'tasks'
    },
    {
      id: 'team_task',
      label: 'Team Task',
      description: 'Internal task for team members',
      icon: <CheckSquare className="w-6 h-6" />,
      color: 'bg-purple-100 text-purple-700 hover:bg-purple-200 border-purple-300',
      section: 'tasks'
    },
    {
      id: 'meeting',
      label: 'Meeting',
      description: 'Schedule a meeting or call',
      icon: <Calendar className="w-6 h-6" />,
      color: 'bg-green-100 text-green-700 hover:bg-green-200 border-green-300',
      section: 'tasks'
    },
    // Approvals & Sign-offs
    {
      id: 'approval_form',
      label: 'Approval',
      description: 'Formal approval with sign-off',
      icon: <CheckCircle className="w-6 h-6" />,
      color: 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-emerald-300',
      section: 'approvals'
    },
    {
      id: 'verification',
      label: 'Verification',
      description: 'Checklist verification task',
      icon: <ShieldCheck className="w-6 h-6" />,
      color: 'bg-teal-100 text-teal-700 hover:bg-teal-200 border-teal-300',
      section: 'approvals'
    },
    {
      id: 'payment_form',
      label: 'Payment',
      description: 'Invoice or payment request',
      icon: <DollarSign className="w-6 h-6" />,
      color: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200 border-yellow-300',
      section: 'approvals'
    },
    // Forms & Data Collection
    {
      id: 'form',
      label: 'Custom Form',
      description: 'Custom form or questionnaire',
      icon: <ClipboardList className="w-6 h-6" />,
      color: 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200 border-indigo-300',
      section: 'forms'
    },
    {
      id: 'survey_form',
      label: 'Survey',
      description: 'Survey or feedback collection',
      icon: <BarChart3 className="w-6 h-6" />,
      color: 'bg-pink-100 text-pink-700 hover:bg-pink-200 border-pink-300',
      section: 'forms'
    }
  ];

  const tabs = [
    { id: 'all' as TabType, label: 'All', icon: <Briefcase className="w-4 h-4" /> },
    { id: 'content' as TabType, label: 'Content & Media', icon: <FileText className="w-4 h-4" /> },
    { id: 'tasks' as TabType, label: 'Tasks', icon: <CheckSquare className="w-4 h-4" /> },
    { id: 'approvals' as TabType, label: 'Approvals', icon: <UserCheck className="w-4 h-4" /> },
    { id: 'forms' as TabType, label: 'Forms', icon: <FileSignature className="w-4 h-4" /> }
  ];

  const filteredCategories = activeTab === 'all'
    ? categories
    : categories.filter(cat => {
        if (activeTab === 'content') return cat.section === 'content' || cat.section === 'media';
        return cat.section === activeTab;
      });

  const getCategoryCounts = (section: TabType) => {
    if (section === 'all') return categories.length;
    if (section === 'content') return categories.filter(c => c.section === 'content' || c.section === 'media').length;
    return categories.filter(c => c.section === section).length;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900">Choose Item Type</h2>
          <p className="text-gray-600 mt-1">Select the type of item you want to create for this project</p>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50 px-6">
          <div className="flex gap-2 -mb-px overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap
                  ${activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab.icon}
                {tab.label}
                <span className={`
                  ml-1 px-2 py-0.5 rounded-full text-xs font-medium
                  ${activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-200 text-gray-600'
                  }
                `}>
                  {getCategoryCounts(tab.id)}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Categories Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => onSelectCategory(category.id)}
                className={`
                  p-4 rounded-lg border-2 ${category.color}
                  transition-all hover:shadow-lg hover:scale-[1.02] text-left
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                `}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    {category.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm mb-1">{category.label}</h3>
                    <p className="text-xs opacity-75 line-clamp-2">{category.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {filteredCategories.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No items in this category</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {filteredCategories.length} {filteredCategories.length === 1 ? 'option' : 'options'} available
          </div>
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
